<!-- src/components/Public/Homepage.vue -->
<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { usePlanStore } from "@/stores/usePlanStore";
// ── PATCH: autenticação para link da brand ────────────────────────────────────
import { useUserStore } from "@/stores/useUserStore";
// ── FIM PATCH ─────────────────────────────────────────────────────────────────

const router = useRouter();

// ── PATCH: destino da brand varia conforme autenticação ──────────────────────
const userStore = useUserStore();
const brandRoute = computed(() =>
  userStore.isAuthenticated
    ? { name: "DashboardHome" }
    : { name: "LoginView" }
);
// ── FIM PATCH ─────────────────────────────────────────────────────────────────

function goToLogin() {
  router.push({ name: "LoginView" });
}

// ── Planos dinâmicos ──────────────────────────────────────────────────────────
const planStore = usePlanStore();
const { plans } = storeToRefs(planStore);
const plansLoading = ref(true);

// Mesmos helpers de PlanCatalogByName.vue
const CHARS_PER_PAGE = 2450;
const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function fmtInt(n) {
  return new Intl.NumberFormat("pt-BR").format(n);
}
function toPages(chars) {
  return Math.floor((chars || 0) / CHARS_PER_PAGE);
}
function planKeyFromName(name) {
  const n = (name || "").toLowerCase().trim();
  if (n === "free") return "free";
  if (n === "pro") return "pro";
  if (n === "plus") return "plus";
  if (n === "edu") return "edu";
  if (n === "premium") return "premium";
  return "other";
}
function planActionLabel(name) {
  const n = (name || "").toLowerCase().trim();
  if (n === "free") return "Incluído";
  if (n === "pro") return "Assinar Pro";
  if (n === "plus") return "Assinar Plus";
  if (n === "edu") return "Assinar Edu";
  return "Selecionar";
}
function isFeatured(name) {
  return (name || "").toLowerCase().trim() === "plus";
}
function getSharingLabel(maxManagedAccounts) {
  if (maxManagedAccounts === 0) return "Titular não compartilha limites";
  if (maxManagedAccounts > 0)
    return `Titular pode compartilhar limites com até ${fmtInt(
      maxManagedAccounts
    )} usuário${maxManagedAccounts !== 1 ? "s" : ""}`;
  return null;
}
function featuresForPlan(planObj) {
  if (!planObj) return [];
  const textChars = planObj.maxTextCharacters || 0;
  const noteChars = planObj.maxNoteCharacters || 0;
  const groupUsers = planObj.maxGroupUser || 0;
  const managed = planObj.maxManagedAccounts ?? 0;
  const canExport = planObj.canExport ?? false;
  const textPages = toPages(textChars);
  const notePages = toPages(noteChars);
  const features = [];
  if (textPages > 0) {
    features.push(
      `${fmtInt(textPages)} página${
        textPages !== 1 ? "s" : ""
      } em texto (${fmtInt(textChars)} caracteres)`
    );
  } else if (textChars > 0) {
    features.push(`${fmtInt(textChars)} caracteres de texto`);
  }
  if (notePages > 0) {
    features.push(
      `${fmtInt(notePages)} página${
        notePages !== 1 ? "s" : ""
      } em notas (${fmtInt(noteChars)} caracteres)`
    );
  } else if (noteChars > 0) {
    features.push(`${fmtInt(noteChars)} caracteres de notas`);
  }
  if (groupUsers > 0) {
    features.push(
      `${fmtInt(groupUsers)} usuário${groupUsers !== 1 ? "s" : ""} pode${
        groupUsers !== 1 ? "m" : ""
      } ser vinculado${groupUsers !== 1 ? "s" : ""} a grupos`
    );
  }
  const sharingLabel = getSharingLabel(managed);
  if (sharingLabel) features.push(sharingLabel);
  if (canExport) features.push("Exportação de notas em PDF");
  return features;
}

// ✅ Um card por nome de plano, apenas ciclo onetime, ordenado por preço asc.
const pricingCards = computed(() => {
  const seen = new Map();
  for (const p of plans.value || []) {
    if (!p.isActive || !p.isPublic) continue;
    if ((p.billingCycle || "").toLowerCase() !== "onetime") continue;
    const name = (p.name || "").trim();
    // Mantém apenas o de menor preço caso haja duplicatas de nome
    if (!seen.has(name) || Number(p.price) < Number(seen.get(name).price)) {
      seen.set(name, p);
    }
  }
  return [...seen.values()]
    .sort((a, b) => Number(a.price) - Number(b.price))
    .map((p) => ({
      raw: p,
      name: p.name,
      nameKey: planKeyFromName(p.name),
      description: p.description || "",
      price: BRL.format(Number(p.price || 0)),
      features: featuresForPlan(p),
    }));
});

onMounted(async () => {
  document.addEventListener("pointerdown", onClickOutsideMobileMenu);

  try {
    await planStore.fetchActivePlans();
  } catch (e) {
    console.error("Falha ao carregar planos na HomePage:", e);
  } finally {
    plansLoading.value = false;
  }
});
// ── Fim planos dinâmicos ──────────────────────────────────────────────────────

const marketingBullets = [
  {
    title: "Você não lê sozinho",
    desc: "Anote seus próprios textos e os compartilhe.",
    icon: "fa-layer-group",
    shot: "/compartilhamento_notas.png",
  },
  {
    title: "Anotações com autoria",
    desc: "Cada contribuição tem autor identificado. Você sabe quem comentou o texto e pode identificar pessoas que agregam ao seu estudo.",
    icon: "fa-id-badge",
    shot: "/screenshot1.png",
  },
  {
    title: "Privacidade sob controle",
    desc: "Você decide o que fica privado e o que pode ser compartilhado. Mantenha textos, atividades e notas só para você, limite a visibilidade por contexto e publique apenas quando fizer sentido, sem expor rascunhos, dúvidas ou trechos sensíveis.",
    icon: "fa-user-shield",
    shot: "/screenshot1.png",
  },
  {
    title: "Destaques inteligentes por Hashtags",
    desc: "Use hashtags para classificar notas, cada uma aplica suas próprias cores nos destaques, transformando o texto em um mapa visual fácil de explorar.",
    icon: "fa-tags",
    shot: "/screenshot1.png",
  },
  {
    title: "Observações do autor sobre notas de terceiros",
    desc: "Se você é o autor do texto ou da atividade, pode privadamente fazer observações em anotações feitas por outras pessoas. Isso permite corrigir interpretações, aprofundar pontos importantes e transformar o texto em um espaço de diálogo orientado pelo próprio autor.",
    icon: "fa-comments",
    shot: "/screenshot1.png",
  },
  {
    title: "Impressão e estudo offline",
    desc: "Imprima textos e notas para estudar offline, revisar com foco e levar para aula, prova ou grupo de estudo, mantendo a formatação e o contexto das marcações.",
    icon: "fa-print",
    shot: "/screenshot1.png",
  },
   {
    title: "Busca que entende o seu estudo",
    desc: "Filtre notas por título, conteúdo, tag, fonte, autor, grupo ou período, e combine quantos filtros quiser de uma vez.",
    icon: "fa-filter",
  },
  
];

const audiences = [
  {
    title: "Estudantes",
    desc: "Compartilhe textos e atividades associadas para receber contribuições que ampliam sua compreensão e aprofundam o estudo.",
    icon: "fa-book",
  },
   {
    title: "Escolas & Professores",
    desc: "Compartilhe textos e atividades com alunos específicos ou turmas inteiras, organizando leituras orientadas, acompanhando interações, efetuando correções e observações e economizando tempo no processo pedagógico.",
    icon: "fa-chalkboard-user",
    shot: "/prints/contas_membro.png",
    highlight: "Com as contas membro, professores e instituições podem cadastrar alunos diretamente e compartilhar seus limites de uso com cada um deles — sem que o aluno precise contratar um plano separado.",
  },
  {
    title: "Homeschoolers",
    desc: "Pais e tutores criam atividades, anotam e dialogam dentro do próprio texto junto com os filhos. A aprendizagem acontece com presença e participação, não apenas supervisão.",
    icon: "fa-people-roof",
  },
  {
    title: "Comentadores",
    desc: "Deixe suas anotações públicas, contribua com leituras de outras pessoas e construa reputação por meio de comentários relevantes e autorais.",
    icon: "fa-pen-nib",
  },
  {
    title: "Pesquisadores",
    desc: "Registre interpretações, cruze ideias, recupere análises anteriores e organize leituras de artigos, livros e materiais técnicos.",
    icon: "fa-microscope",
  },
  {
    title: "Grupos de leitura",
    desc: "Transforme cada texto em um espaço de conversa organizada, com contribuições vinculadas ao conteúdo lido.",
    icon: "fa-users",
  },
  {
    title: "Leitores críticos",
    desc: "Vá além da leitura silenciosa: compare interpretações, acompanhe notas de outros leitores e aprofunde sua análise.",
    icon: "fa-book-open-reader",
  },
  {
    title: "Criadores de conteúdo",
    desc: "Organize ideias extraídas de textos, artigos, livros e materiais técnicos para transformar leitura em produção autoral.",
    icon: "fa-lightbulb",
  },
];

const howItWorks = [
  {
    num: 1,
    title: "Crie ou importe textos",
    desc: "Cadastre metadados, cole o texto ou anexe um arquivo txt e defina permissões (usuários e grupos).",
    startTime: 11,
    endTime: 26,
  },
  {
    num: 2,
    title: "Crie atividades no texto",
    desc: "Cadastre atividades com indicações de progresso, datas e permissões (usuários e grupos) para organizar seu estudo.",
    startTime: 26,
    endTime: 37,
  },
  {
    num: 3,
    title: "Anote dentro da atividade",
    desc: "Selecione um trecho com o mouse e crie notas vinculadas à atividade: informe título, corpo e fonte, crie ou selecione uma ou mais hashtags (se quiser) e defina o acesso (todos ou por usuários e grupos).",
    startTime: 37,
    endTime: 96,
  },
  {
    num: 4,
    title: "Filtre e explore suas notas",
    desc: "Encontre rapidamente notas pessoais ou de terceiros; e também busque por usuário, título, conteúdo ou hashtag.",
    startTime: 96,
    endTime: 109,
  },
  {
    num: 5,
    title: "Exporte e estude offline",
    desc: "Gere PDFs com o texto e as notas, preservando destaques, índices de rodapé e o contexto das marcações.",
    startTime: 109,
    endTime: null,
  },
];

const faqs = [
  {
    q: "1. Com o plano Free é possível testar todas as funcionalidades do Litterae?",
    a: "Sim. Todas as funcionalidades estão disponíveis no plano Free — a única diferença em relação aos planos pagos é a cota de uso (quantidade de caracteres disponíveis para textos e notas).",
  },

  // ✅ NOVO: tempo de acesso à plataforma
  {
    q: "2. Por quanto tempo posso usar a plataforma?",
    a: "O plano Free permite acesso contínuo à plataforma, sem prazo de expiração, porém com limites reduzidos de uso. Nos planos pagos, o acesso depende da modalidade escolhida: no pagamento avulso, a assinatura é válida por 30 dias; no plano recorrente mensal, o acesso também é renovado a cada 30 dias, de forma automática, enquanto a assinatura estiver ativa.",
  },

  // ✅ NOVO: formas de pagamento aceitas
  {
    q: "3. Quais formas de pagamento são aceitas?",
    a: "Você pode assinar a plataforma com cartão de crédito ou PIX. No cartão de crédito, é possível escolher entre pagamento único ou assinatura recorrente mensal. No PIX, o pagamento é feito de forma avulsa, com acesso válido pelo período de 30 dias.",
  },

  {
    q: "4. O Litterae é para literatura ou também serve para materiais técnicos?",
    a: "Serve para ambos. Você pode trabalhar com textos literários, acadêmicos ou informativos — a lógica de anotações e compartilhamento é a mesma.",
  },
  {
    q: "5. Consigo controlar quem vê minhas anotações?",
    a: "Sim. Você pode manter conteúdo privado ou permitir que outras pessoas vejam (inclusive por grupos ou usuários selecionados).",
  },
  {
    q: "6. Consigo saber quem escreveu uma anotação e entrar em contato?",
    a: "Sim. As anotações têm autoria. E, se o autor tiver liberado, você poderá contatá-lo via WhatsApp, telefone, Instagram, Facebook ou LinkedIn.",
  },
  {
    q: "7. O que é uma conta titular e uma conta membro?",
    a: "Titular é quem contrata um plano no Litterae e possui uma cota própria de uso. Membro é um usuário cadastrado pelo titular — ele acessa a plataforma com login próprio, mas anota consumindo a cota do titular que o criou, sem precisar pagar nada.",
  },
  {
    q: "8. O que é uma atividade no Litterae?",
    a: "É um espaço de leitura e anotação vinculado a um texto. Você define as permissões de visibilidade, edição e seleção: pública, privada ou personalizada — restrita a usuários ou grupos específicos.",
  },
  {
    q: "9. Com quem posso compartilhar um texto, atividade ou anotação?",
    a: "Com qualquer usuário do Litterae, com um grupo específico ou com contas membro cadastradas por você. Contas membro não precisam contratar nem pagar nada — elas anotam consumindo a cota do titular que as criou.",
  },
  {
    q: "10. Como é possível criar uma atividade colaborativa no Litterae?",
    a: "Crie um texto, associe uma atividade a ele e defina as permissões de visibilidade para as pessoas que quiser: qualquer usuário do Litterae, um grupo específico ou contas membro cadastradas por você. A partir daí, todos os participantes podem anotar no mesmo texto, cada um com autoria identificada.",
  },
  {
    q: "11. Como crio uma atividade para meus alunos anotarem?",
    a: "Crie o texto e uma atividade associada, cadastre seus alunos como contas membro informando os e-mails deles e envie as senhas. Depois crie um grupo — por exemplo, \"Turma A\" — e atribua permissão de visibilidade a ele. Assim, os alunos acessam a atividade e anotam consumindo sua cota, sem custo para eles. Você pode criar quantas atividades quiser para organizar turmas ou leituras diferentes.",
  },
  {
    q: "12. É possível criar uma atividade compartilhada com outros usuários titulares do Litterae?",
    a: "Sim. Basta conceder permissão de visibilidade da atividade para os usuários ou grupos desejados. Nesse caso, cada participante anota consumindo a própria cota — não a de quem criou a atividade. Contas membro são exclusivas para quem quer ceder cota a terceiros, como professores fazem com alunos.",
  },
  {
    q: "13. Os preços exibidos estão atualizados?",
    a: "Sim. Eles refletem os valores atuais dos planos disponíveis.",
  },
];

const year = computed(() => new Date().getFullYear());

// ── Carrossel do dashboard ────────────────────────────────────────────────────
const dashSlides = [
  { src: "/prints/DashboardView1.png", alt: "Painel — visão geral e últimos textos anotados" },
  { src: "/prints/DashboardView2.png", alt: "Painel — feed de atividades de quem você segue" },
];
const dashSlideIndex = ref(0);

function dashPrev() {
  dashSlideIndex.value =
    (dashSlideIndex.value - 1 + dashSlides.length) % dashSlides.length;
}
function dashNext() {
  dashSlideIndex.value = (dashSlideIndex.value + 1) % dashSlides.length;
}
function dashGoTo(i) {
  dashSlideIndex.value = i;
}

// ── Modal de imagem ampliada (carrossel da comunidade) ───────────────────────
// activeShot armazena o índice dentro de dashSlides (null = fechado).
const activeShot = ref(null);

function openShot(idx) {
  activeShot.value = idx;
}

function shotPrev() {
  if (activeShot.value === null) return;
  activeShot.value = (activeShot.value - 1 + dashSlides.length) % dashSlides.length;
}

function shotNext() {
  if (activeShot.value === null) return;
  activeShot.value = (activeShot.value + 1) % dashSlides.length;
}
// ── FIM modal de imagem ampliada ─────────────────────────────────────────────

// ── Carrossel da seção de pesquisa ───────────────────────────────────────────
const searchSlides = [
  {
    src: "/prints/search_texts.png",
    alt: "Pesquisa de textos — filtros por autor, gênero, assunto e mais",
    caption: "Pesquisa de Textos — filtre por título, autor, gênero, assunto e criador",
  },
  {
    src: "/prints/search_activities.png",
    alt: "Pesquisa de atividades — filtros por progresso, período e grupo",
    caption: "Pesquisa de Atividades — acompanhe progresso, datas e permissões",
  },
  {
    src: "/prints/search_notes.png",
    alt: "Pesquisa de notas — 12 filtros combinados incluindo hashtag e conteúdo",
    caption: "Pesquisa de Notas — 12 critérios simultâneos com destaque do termo buscado",
  },
];
const searchSlideIndex = ref(0);

function searchPrev() {
  searchSlideIndex.value =
    (searchSlideIndex.value - 1 + searchSlides.length) % searchSlides.length;
}
function searchNext() {
  searchSlideIndex.value =
    (searchSlideIndex.value + 1) % searchSlides.length;
}
function searchGoTo(i) {
  searchSlideIndex.value = i;
}
// ── FIM carrosséis ────────────────────────────────────────────────────────────

// const howItWorksVideoUrl = ref(
//   "https://youtu.be/qLVyYmtGt7I?si=Riqafbv0IpC5y9mm"
// );

const howItWorksVideoUrl = ref(
  "https://youtu.be/OPSDnuI8LuA"
);

// const howItWorksVideoUrl = ref(
//   "videos/Litterae - Montagem bruta completa.mp4"
// );
const activeVideo = ref(false);
const videoStartTime = ref(null);
const videoEndTime = ref(null);
const videoElRef = ref(null);

const videoModalTitle = ref("Como funciona (fluxo completo)");
const videoModalSub = ref("1 vídeo para os 5 passos");

const scrollYBeforeLock = ref(0);
const bodyPaddingRightBeforeLock = ref("");

function lockBodyScroll() {
  scrollYBeforeLock.value = window.scrollY || window.pageYOffset || 0;

  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;

  bodyPaddingRightBeforeLock.value = document.body.style.paddingRight || "";
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  document.documentElement.classList.add("modal-open");
  document.body.classList.add("modal-open");
}

function unlockBodyScroll() {
  document.documentElement.classList.remove("modal-open");
  document.body.classList.remove("modal-open");

  document.body.style.paddingRight = bodyPaddingRightBeforeLock.value || "";

  const y = scrollYBeforeLock.value || 0;
  window.scrollTo(0, y);
}

function closeShot() {
  activeShot.value = null;
}

// ── PATCH: activeSingleShot — imagens avulsas (audience-card, sem navegação) ─
const activeSingleShot = ref(null); // { src, alt } | null

function closeSingleShot() {
  activeSingleShot.value = null;
}
// ── FIM PATCH ─────────────────────────────────────────────────────────────────

// ── PATCH: teclas de navegação no modal de shot ──────────────────────────────
function onShotKeydown(e) {
  if (activeShot.value === null) return;
  if (e.key === "ArrowLeft")  { e.preventDefault(); shotPrev(); }
  if (e.key === "ArrowRight") { e.preventDefault(); shotNext(); }
}
// ── FIM PATCH ─────────────────────────────────────────────────────────────────

function getYouTubeId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);

    if (u.hostname.includes("youtu.be")) {
      return (u.pathname || "").replace("/", "").trim();
    }

    const v = u.searchParams.get("v");
    if (v) return v;

    const parts = (u.pathname || "").split("/").filter(Boolean);
    const embedIdx = parts.indexOf("embed");
    if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];

    const shortsIdx = parts.indexOf("shorts");
    if (shortsIdx >= 0 && parts[shortsIdx + 1]) return parts[shortsIdx + 1];

    return "";
  } catch {
    return "";
  }
}

const howItWorksEmbedUrl = computed(() => {
  const id = getYouTubeId(howItWorksVideoUrl.value);
  if (id) {
    const start = videoStartTime.value != null ? `&start=${videoStartTime.value}` : "";
    const end = videoEndTime.value != null ? `&end=${videoEndTime.value}` : "";
    return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1${start}${end}`;
  }
  if (howItWorksVideoUrl.value) return howItWorksVideoUrl.value;
  return "";
});

const isLocalVideo = computed(() =>
  !!howItWorksEmbedUrl.value && !howItWorksEmbedUrl.value.startsWith("https://www.youtube.com")
);

function openHowItWorksVideo(title, sub, startTime = null, endTime = null) {
  if (!howItWorksVideoUrl.value) return;

  videoModalTitle.value = title || "Como funciona (fluxo completo)";
  videoModalSub.value = sub || "1 vídeo para os 5 passos";
  videoStartTime.value = startTime;
  videoEndTime.value = endTime;

  activeVideo.value = true;
}

function closeVideo() {
  activeVideo.value = false;
  videoStartTime.value = null;
  videoEndTime.value = null;
}

function onVideoLoaded() {
  if (videoElRef.value && videoStartTime.value != null) {
    videoElRef.value.currentTime = videoStartTime.value;
  }
}

function onVideoTimeUpdate() {
  if (videoElRef.value && videoEndTime.value != null && videoElRef.value.currentTime >= videoEndTime.value) {
    videoElRef.value.pause();
  }
}

function closeAllModals() {
  closeShot();
  closeSingleShot();
  closeVideo();
}

function onGlobalKeydown(e) {
  if (e.key === "Escape") closeAllModals();
  // ── PATCH: setas navegam no modal de imagem ampliada ──────────────────────
  onShotKeydown(e);
  // ── FIM PATCH ─────────────────────────────────────────────────────────────
}

watch(
  () => activeShot.value !== null || !!activeSingleShot.value || !!activeVideo.value,
  (isOpen) => {
    if (isOpen) {
      lockBodyScroll();
      window.addEventListener("keydown", onGlobalKeydown);
    } else {
      unlockBodyScroll();
      window.removeEventListener("keydown", onGlobalKeydown);
    }
  }
);

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onClickOutsideMobileMenu);
  unlockBodyScroll();
  window.removeEventListener("keydown", onGlobalKeydown);
});

const isTopRow = (idx) => idx <= 2;
const showRightArrow = (idx) => isTopRow(idx) && idx !== 2;
const isDownConnector = (idx) => idx === 2;
const showLeftArrow = (idx) => idx === 3;

const mobileMenuOpen = ref(false);
const topbarRef = ref(null);

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

function onClickOutsideMobileMenu(event) {
  if (!mobileMenuOpen.value) return;
  if (!topbarRef.value) return;

  const target = event.target;
  if (target && topbarRef.value.contains(target)) return;

  closeMobileMenu();
}

const hasHowItWorksVideo = computed(() => !!howItWorksVideoUrl.value);
</script>

<template>
  <div class="public-home">
    <header ref="topbarRef" class="topbar">
      <div class="topbar-inner">
        <!-- ── PATCH: topbar-brand linkada conforme autenticação ── -->
        <RouterLink :to="brandRoute" class="topbar-brand">
          <img
            src="/litterae_pena_fundo_transparente.png"
            alt="Litterae Logo"
            class="brand-logo"
          />

          <div class="brand-text">
            <div class="brand-name">Litterae</div>
            <div class="brand-tagline">Você não lê sozinho.</div>
          </div>
        </RouterLink>
        <!-- ── FIM PATCH ── -->

        <nav class="topbar-section-links" aria-label="Navegação da página">
          <!-- <a class="topbar-link" href="#how-it-works">Como funciona</a> -->
          <a class="topbar-link" href="#audience">Para quem é</a>
          <a class="topbar-link" href="#reading-club">Clube de Leitura</a>
          <a class="topbar-link" href="#community">Comunidade</a>
          <a class="topbar-link" href="#search">Pesquisas</a>
          <a class="topbar-link" href="#pricing">Planos</a>
        </nav>

        <nav class="nav-links" aria-label="Ações da conta">
          <RouterLink
            :to="{ name: 'LoginView' }"
            class="nav-btn nav-btn--ghost"
          >
            Entrar
          </RouterLink>

          <RouterLink
            :to="{ name: 'LoginView' }"
            class="nav-btn nav-btn--solid"
          >
            Criar Conta
          </RouterLink>
        </nav>

        <div class="mobile-menu">
          <button
            class="mobile-menu-toggle"
            type="button"
            aria-label="Abrir menu de navegação"
            aria-controls="mobile-home-menu"
            :aria-expanded="mobileMenuOpen"
            @click="toggleMobileMenu"
          >
            <i
              class="fa-solid"
              :class="mobileMenuOpen ? 'fa-xmark' : 'fa-bars'"
              aria-hidden="true"
            ></i>
          </button>

          <nav
            v-if="mobileMenuOpen"
            id="mobile-home-menu"
            class="mobile-dropdown"
            aria-label="Menu de navegação mobile"
          >
            <a
              class="mobile-dropdown-link"
              href="#audience"
              @click="closeMobileMenu"
            >
              Para quem é
            </a>

            <a
              class="mobile-dropdown-link"
              href="#how-it-helps"
              @click="closeMobileMenu"
            >
              Como o Litterae ajuda
            </a>

            <a
              class="mobile-dropdown-link"
              href="#how-it-works"
              @click="closeMobileMenu"
            >
              Como funciona
            </a>

            <a
              class="mobile-dropdown-link"
              href="#reading-club"
              @click="closeMobileMenu"
            >
              Clube de Leitura
            </a>

            <a
              class="mobile-dropdown-link"
              href="#community"
              @click="closeMobileMenu"
            >
              Comunidade
            </a>

            <a
              class="mobile-dropdown-link"
              href="#search"
              @click="closeMobileMenu"
            >
              Pesquisas
            </a>

            <a
              class="mobile-dropdown-link"
              href="#pricing"
              @click="closeMobileMenu"
            >
              Planos
            </a>

            <div class="mobile-dropdown-divider" aria-hidden="true"></div>

            <RouterLink
              :to="{ name: 'LoginView' }"
              class="mobile-dropdown-link mobile-dropdown-link--ghost"
              @click="closeMobileMenu"
            >
              Entrar
            </RouterLink>

            <RouterLink
              :to="{ name: 'LoginView' }"
              class="mobile-dropdown-link mobile-dropdown-link--solid"
              @click="closeMobileMenu"
            >
              Criar Conta
            </RouterLink>
          </nav>
        </div>
      </div>
    </header>

    <section class="hero">
      <div class="container hero-grid">
        <div class="hero-left">
          <h1 class="hero-title">Você não lê sozinho</h1>

          <p class="hero-subtitle">
            Anote, compartilhe e colabore em cima de qualquer texto. O Litterae é o lugar onde sua leitura encontra a de outras pessoas.
          </p>

          <div class="hero-cta">
            <button
              class="nav-btn nav-btn--solid hero-cta-btn"
              type="button"
              @click="goToLogin"
            >
              Experimente Litterae
            </button>
          </div>

          <p class="hero-login">
            Já possui uma conta?
            <RouterLink :to="{ name: 'LoginView' }" class="login-link">
              <strong>Faça login.</strong>
            </RouterLink>
          </p>
        </div>
      </div>
    </section>

    <section class="section section-after-hero">
      <div class="container">
        <img
          src="/prints/tela_de_anotacoes.png"
          alt="Litterae"
          loading="lazy"
          class="hero-image"
        />

        <div class="demo-video-wrap">
          <button
            v-if="hasHowItWorksVideo"
            type="button"
            class="how-video-cta"
            @click="openHowItWorksVideo('O Litterae em ação', 'Demonstração')"
          >
            <i class="fa-solid fa-circle-play"></i>
            Ver o Litterae em ação
          </button>
        </div>
      </div>
    </section>

    <section id="how-it-helps" class="section hero-right">
      <div class="container">
        <div class="section-head">
          <h2>Como o Litterae ajuda</h2>
          <p>
            Autoria, compartilhamento e, quando permitido pelo autor, canais de
            contato.
          </p>

        </div>

        <div class="help-grid">
          <article
            v-for="(b, idx) in marketingBullets"
            :key="idx"
            class="info-card info-card--video help-card"
            aria-label="Card informativo"
          >
            <div class="info-icon">
              <i class="fa-solid" :class="b.icon"></i>
            </div>
 
            <h3>{{ b.title }}</h3>
            <p>{{ b.desc }}</p>
          </article>
 
        </div>
      </div>
    </section>

    <section id="how-it-works" class="section section--alt section--alt-off">
      <div class="container">
        <div class="section-head">
          <h2>Como funciona</h2>
          <p>
            Fluxo simples, para você gastar energia no que importa: ler, estudar
            e aproveitar contribuições relevantes.
          </p>

          <button
            v-if="hasHowItWorksVideo"
            class="how-video-cta"
            type="button"
            @click="
              openHowItWorksVideo(
                'Como funciona (fluxo completo)',
                '1 vídeo para os 5 passos'
              )
            "
          >
            <i class="fa-solid fa-circle-play"></i>
            Ver vídeo do fluxo
          </button>
        </div>

        <div class="flow-steps">
          <div
            v-for="(s, idx) in howItWorks"
            :key="`${s.num}-${idx}`"
            class="step flow-step"
            :class="{
 'flow-step--video': hasHowItWorksVideo,
              'flow-step--row2-col3': idx === 3,
              'flow-step--row2-col2': idx === 4,
            }"
            role="button"
            tabindex="0"
            :aria-label="
              hasHowItWorksVideo
                ? `Assistir vídeo do fluxo: ${s.title}`
                : s.title
            "
            @click="openHowItWorksVideo(s.title, `Passo ${s.num} de 5`, s.startTime, s.endTime)"
            @keydown.enter="openHowItWorksVideo(s.title, `Passo ${s.num} de 5`, s.startTime, s.endTime)"
            @keydown.space.prevent="openHowItWorksVideo(s.title, `Passo ${s.num} de 5`, s.startTime, s.endTime)"
          >
            <div class="step-top">
              <div class="step-num">{{ s.num }}</div>
            </div>

            <div class="step-title">{{ s.title }}</div>
            <div class="step-desc">{{ s.desc }}</div>

            <span
              v-if="showRightArrow(idx)"
              class="flow-arrow flow-arrow--right"
              aria-hidden="true"
            />
            <span
              v-if="isDownConnector(idx)"
              class="flow-arrow flow-arrow--down"
              aria-hidden="true"
            />
            <span
              v-if="showLeftArrow(idx)"
              class="flow-arrow flow-arrow--left"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ── seção Pesquisa detalhada ─────────────────────────────────────── -->
    <section id="search" class="section section--plain">
      <div class="container">

        <div class="section-head">
          <h2>Pesquisa detalhada por texto, nota e atividade</h2>
          <p>
            Combine quantos filtros quiser ao mesmo tempo — por autor, tag,
            grupo, data, progresso e muito mais. Não é uma busca simples:
            é uma consulta multidimensional em tempo real.
          </p>
        </div>

        <!-- Cards das três entidades -->
        <div class="cards-3 search-cards">

          <!-- Card Textos -->
          <article class="info-card search-card">
            <div class="info-icon search-icon--texts">
              <i class="fa-solid fa-book-open"></i>
            </div>
            <h3>Pesquisa de Textos</h3>
            <p>
              Encontre qualquer texto pelo título, subtítulo, autor, gênero,
              assunto, criador ou fonte — com ou sem filtro de "meus textos".
            </p>
            <ul class="search-filter-list">
              <li><i class="fa-solid fa-circle-dot"></i> Título e subtítulo</li>
              <li><i class="fa-solid fa-circle-dot"></i> Autor e gênero literário</li>
              <li><i class="fa-solid fa-circle-dot"></i> Assunto (Poesia, Prosa, Teatro…)</li>
              <li><i class="fa-solid fa-circle-dot"></i> Criador e fonte</li>
              <li><i class="fa-solid fa-circle-dot"></i> Data de criação</li>
              <li><i class="fa-solid fa-circle-dot"></i> Toggle "Meus textos"</li>
            </ul>
          </article>

          <!-- Card Atividades -->
          <article class="info-card search-card">
            <div class="info-icon search-icon--activities">
              <i class="fa-solid fa-file-signature"></i>
            </div>
            <h3>Pesquisa de Atividades</h3>
            <p>
              Filtre atividades por progresso, período, criador, texto
              vinculado e permissões — veja exatamente o que cada aluno ou
              grupo está fazendo.
            </p>
            <ul class="search-filter-list">
              <li><i class="fa-solid fa-circle-dot"></i> Título da atividade e do texto</li>
              <li><i class="fa-solid fa-circle-dot"></i> Progresso (5 estados)</li>
              <li><i class="fa-solid fa-circle-dot"></i> Data de início e fim</li>
              <li><i class="fa-solid fa-circle-dot"></i> Criador (ID ou nome)</li>
              <li><i class="fa-solid fa-circle-dot"></i> Grupo e usuário com permissão</li>
              <li><i class="fa-solid fa-circle-dot"></i> Toggle "Minhas atividades"</li>
            </ul>
          </article>

          <!-- Card Notas -->
          <article class="info-card search-card search-card--featured">
            <div class="search-card-badge">Mais filtros</div>
            <div class="info-icon search-icon--notes">
              <i class="fa-solid fa-note-sticky"></i>
            </div>
            <h3>Pesquisa de Notas</h3>
            <p>
              A pesquisa mais granular: até 12 critérios combinados em uma
              única consulta — incluindo busca dentro do corpo da nota com
              destaque visual do termo encontrado.
            </p>
            <ul class="search-filter-list">
              <li><i class="fa-solid fa-circle-dot"></i> Título e conteúdo da nota</li>
              <li><i class="fa-solid fa-circle-dot"></i> Hashtag, fonte e criador</li>
              <li><i class="fa-solid fa-circle-dot"></i> Título do texto e da atividade</li>
              <li><i class="fa-solid fa-circle-dot"></i> Grupo e usuário com permissão</li>
              <li><i class="fa-solid fa-circle-dot"></i> Período de criação</li>
              <li><i class="fa-solid fa-circle-dot"></i> Só com conteúdo / com observação / notas avulsas</li>
              <li><i class="fa-solid fa-circle-dot"></i> Toggle "Minhas notas"</li>
            </ul>
          </article>

        </div>

        <!-- Exemplos práticos -->
        <div class="search-examples-wrap">
          <p class="search-examples-title">
            <i class="fa-solid fa-circle-info"></i>
            Exemplos práticos de consultas combinadas
          </p>

          <div class="search-examples-grid">

            <div class="search-example-card">
              <span class="search-example-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <p>
                Notas da <strong>Maria</strong>, entre <strong>01/03 e 30/03</strong>,
                com a palavra <em>protagonista</em> no corpo, marcadas com a tag
                <strong>#dúvida</strong>, vinculadas ao texto
                <strong>Dom Casmurro</strong>, visíveis para o grupo
                <strong>Turma A</strong>.
              </p>
            </div>

            <div class="search-example-card">
              <span class="search-example-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <p>
                Atividades criadas por <strong>mim</strong>, com progresso
                <strong>Concluída para Correção</strong>, vinculadas à
                <em>Odisseia</em>, com pelo menos uma nota registrada.
              </p>
            </div>

            <div class="search-example-card">
              <span class="search-example-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <p>
                Apenas <strong>minhas notas avulsas</strong> (sem vínculo com
                trecho do texto) que possuem <strong>observação do autor</strong>
                ainda não lida.
              </p>
            </div>

          </div>
        </div>

        <!-- Carrossel das três pesquisas -->
        <div class="search-carousel-wrap">

          <p class="search-carousel-label">
            <i class="fa-solid fa-display"></i>
            Veja como as pesquisas funcionam na prática
          </p>

          <div class="search-carousel">
            <div class="search-carousel__slides">
              <img
                :src="searchSlides[searchSlideIndex].src"
                :alt="searchSlides[searchSlideIndex].alt"
                class="search-carousel__img"
                loading="lazy"
              />
            </div>

            <button
              class="dash-carousel__btn dash-carousel__btn--prev"
              type="button"
              aria-label="Slide anterior"
              @click="searchPrev"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button
              class="dash-carousel__btn dash-carousel__btn--next"
              type="button"
              aria-label="Próximo slide"
              @click="searchNext"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>

          <div class="dash-carousel__dots search-carousel__dots-row">
            <button
              v-for="(slide, i) in searchSlides"
              :key="i"
              class="dash-carousel__dot"
              :class="{ 'dash-carousel__dot--active': i === searchSlideIndex }"
              type="button"
              :aria-label="`Ver slide ${i + 1}`"
              @click="searchGoTo(i)"
            />
          </div>

          <p class="search-carousel__slide-caption">
            {{ searchSlides[searchSlideIndex].caption }}
          </p>

        </div>

      </div>
    </section>
    <!-- ── FIM seção Pesquisa detalhada ──────────────────────────────────── -->

    <section id="audience" class="section section--alt">
      <div class="container">
        <div class="section-head">
          <h2>Para quem é o Litterae</h2>
          <p>
            Um espaço para organizar leitura e estudo, enriquecendo sua
            compreensão com anotações de outras pessoas.
          </p>
        </div>

        <div class="cards-3">
          <article
            v-for="(a, idx) in audiences"
            :key="idx"
            class="info-card audience-card audience-card--hover"
          >
            <div class="info-icon">
              <i class="fa-solid" :class="a.icon"></i>
            </div>
 
            <h3>{{ a.title }}</h3>
            <p>{{ a.desc }}</p>
 
            <div v-if="a.highlight" class="audience-highlight">
              <i class="fa-solid fa-circle-info audience-highlight__icon"></i>
              <span>{{ a.highlight }}</span>
            </div>
 
            <img
              v-if="a.shot"
              :src="a.shot"
              :alt="a.title"
              class="audience-card-shot"
              loading="lazy"
              @click="activeSingleShot = { src: a.shot, alt: a.title }"
            />
          </article>
        </div>
      </div>
    </section>

    <!-- ── seção Clube de Leitura ────────────────────────────────────────── -->
    <section id="reading-club" class="section section--plain">
      <div class="container">

        <div class="section-head">
          <h2>Clube de Leitura</h2>
          <p>
            Crie um espaço de leitura colaborativa para sua turma — sem custo
            para os alunos.
          </p>
        </div>

        <!-- Narrativa -->
        <p class="reading-club__narrative">
          Um professor quer criar um clube de leitura da
          <em>Odisseia</em> com seus 10 alunos. No Litterae, ela cria o texto,
          abre uma atividade chamada "Clube de Leitura da Odisseia", cadastra
          os alunos como contas membro e envia as senhas. A partir daí, cada
          aluno anota diretamente na atividade dela — consumindo a cota da
          professora, sem precisar contratar nada.
        </p>

        <!-- Passos -->
        <div class="reading-club__steps">
          <div class="rc-step">
            <div class="rc-step__num">1</div>
            <p class="rc-step__text">Crie o texto e a atividade</p>
          </div>
          <div class="rc-step">
            <div class="rc-step__num">2</div>
            <p class="rc-step__text">Cadastre contas membro com o e-mail dos alunos</p>
          </div>
          <div class="rc-step">
            <div class="rc-step__num">3</div>
            <p class="rc-step__text">Envie as senhas para os alunos</p>
          </div>
          <div class="rc-step">
            <div class="rc-step__num">4</div>
            <p class="rc-step__text">Os alunos anotam na sua atividade</p>
          </div>
          <div class="rc-step">
            <div class="rc-step__num">5</div>
            <p class="rc-step__text">Expanda para novas turmas criando mais atividades</p>
          </div>
        </div>

        <!-- Highlight -->
        <div class="audience-highlight reading-club__highlight">
          <i class="fa-solid fa-circle-info audience-highlight__icon"></i>
          <span>
            Os alunos anotam consumindo a cota do professor. Nenhum custo
            adicional para quem aprende.
          </span>
        </div>

      </div>
    </section>
    <!-- ── FIM seção Clube de Leitura ──────────────────────────────────────── -->

    <!-- ── seção Comunidade / Dashboard ─────────────────────────────── -->
    <section id="community" class="section section--alt community-section">
      <div class="container">

        <div class="section-head">
          <h2>Você faz parte de uma rede de leitores</h2>
          <p>
            O Litterae não é só um caderno de anotações — é um espaço onde
            leitores se encontram, se seguem e acompanham uns aos outros em
            tempo real.
          </p>
        </div>

        <div class="cards-3 community-cards">
          <article class="info-card community-card">
            <div class="info-icon community-icon--follow">
              <i class="fa-solid fa-users"></i>
            </div>
            <h3>Siga quem lê como você</h3>
            <p>
              Encontre leitores que anotam os mesmos textos, siga-os com um
              clique e acompanhe tudo que eles descobrem — sem sair da sua
              leitura.
            </p>
          </article>

          <article class="info-card community-card">
            <div class="info-icon community-icon--feed">
              <i class="fa-solid fa-rss"></i>
            </div>
            <h3>Feed de atividade em tempo real</h3>
            <p>
              Veja quando alguém que você segue cria uma nota, abre uma
              atividade ou anota um novo texto. Cada ação vira um ponto de
              conexão entre leitores.
            </p>
          </article>

          <article class="info-card community-card">
            <div class="info-icon community-icon--rank">
              <i class="fa-solid fa-chart-line"></i>
            </div>
            <h3>Evolução visível, progresso real</h3>
            <p>
              Acompanhe sua própria evolução em gráficos por dia, mês ou ano.
              Veja no ranking quem mais anotou e use isso como motivação para
              ler mais.
            </p>
          </article>
        </div>

        <div class="community-showcase">

          <div class="community-copy">

            <p class="community-copy__lead">
              Veja na prática o que acontece quando você começa a seguir outros
              leitores:
            </p>

            <ul class="community-examples">
              <li class="community-example">
                <span class="community-example__icon">
                  <i class="fa-solid fa-user-plus"></i>
                </span>
                <div>
                  <strong>Pedro seguiu Maria</strong> depois de ler uma nota
                  dela sobre <em>Dom Casmurro</em>. Agora ele vê, no feed
                  "Atividades de quem você segue", cada novo texto, atividade e
                  nota que ela cria, edita ou deleta — em tempo real.
                </div>
              </li>

              <li class="community-example">
                <span class="community-example__icon">
                  <i class="fa-solid fa-trophy"></i>
                </span>
                <div>
                  <strong>João chegou ao top 3 do ranking</strong> dos que mais
                  anotaram no mês — e percebeu que estava lendo mais do que
                  antes.
                </div>
              </li>

              <li class="community-example">
                <span class="community-example__icon">
                  <i class="fa-solid fa-chart-line"></i>
                </span>
                <div>
                  <strong>Ana acompanha sua própria evolução</strong> pelo
                  gráfico de notas por mês. Em seis semanas, triplicou sua
                  produção de anotações.
                </div>
              </li>
            </ul>

            <button
              class="nav-btn nav-btn--solid community-cta-btn"
              type="button"
              @click="goToLogin"
            >
              <i class="fa-solid fa-users"></i>
              Entrar na comunidade
            </button>
          </div>

          <div class="community-shot-wrap">

            <div class="dash-carousel">
              <img
                :src="dashSlides[dashSlideIndex].src"
                :alt="dashSlides[dashSlideIndex].alt"
                class="community-shot dash-carousel__img"
                loading="lazy"
                @click="openShot(dashSlideIndex)"
              />

              <button
                class="dash-carousel__btn dash-carousel__btn--prev"
                type="button"
                aria-label="Imagem anterior"
                @click="dashPrev"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>

              <button
                class="dash-carousel__btn dash-carousel__btn--next"
                type="button"
                aria-label="Próxima imagem"
                @click="dashNext"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>

            <div class="dash-carousel__dots">
              <button
                v-for="(slide, i) in dashSlides"
                :key="i"
                class="dash-carousel__dot"
                :class="{ 'dash-carousel__dot--active': i === dashSlideIndex }"
                type="button"
                :aria-label="`Ver imagem ${i + 1}`"
                @click="dashGoTo(i)"
              />
            </div>

            <p class="community-shot-hint">
              <i class="fa-solid fa-magnifying-glass-plus"></i>
              Clique na imagem para ampliar
            </p>
          </div>
        </div>


      </div>
    </section>
    <!-- ── FIM seção Comunidade ────────────────────────────────────────────── -->

    <div class="section-divider" aria-hidden="true"></div>

    <section class="pricing-section pricing-section--plain" id="pricing">
      <div class="pricing-inner">
        <div class="section-title">
        <h2>Planos adaptados aos seus estudos</h2>
        <p>Escolha a melhor opção para sua jornada de aprendizado</p>
      </div>

        <div v-if="plansLoading" class="pricing-loading">
        <svg
          class="animate-spin h-7 w-7"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>

        <div v-else class="pricing-grid">
        <div
          v-for="card in pricingCards"
          :key="card.name"
          class="price-card"
          :class="[`plan-${card.nameKey}`, { featured: isFeatured(card.name) }]"
        >
          <div v-if="isFeatured(card.name)" class="popular-tag">
            Mais Popular
          </div>

          <div class="card-header">
            <h3>{{ card.name }}</h3>
          </div>

          <p class="description">{{ card.description || "—" }}</p>

          <div class="price">{{ card.price }}</div>
          <div v-if="card.nameKey === 'free'" class="free-badge">
            <i class="fas fa-bolt"></i> Acesso imediato
          </div>

          <ul class="features">
            <li v-for="(f, i) in card.features" :key="i">
              <i class="fas fa-check"></i>
              {{ f }}
            </li>
          </ul>

          <button
            class="btn-plan"
            :class="`btn-${card.nameKey}`"
            type="button"
            @click="goToLogin"
          >
            {{ planActionLabel(card.name) }}
          </button>
        </div>
        </div>
      </div>
    </section>

    <section class="section section--alt faq-section">
      <div class="container">
        <div class="section-head">
          <h2>Perguntas frequentes</h2>
          <p>Respostas diretas, sem prometer o que não existe.</p>
        </div>

        <div class="faq">
          <details v-for="(f, idx) in faqs" :key="idx" class="faq-item">
            <summary class="faq-q">
              <span>{{ f.q }}</span>
              <i class="fa-solid fa-chevron-down"></i>
            </summary>
            <div class="faq-a">{{ f.a }}</div>
          </details>
        </div>
      </div>
    </section>

    <!-- ── seção CTA de encerramento ───────────────────────────────── -->
    <section class="cta-section">
      <div class="container cta-inner">
        <h2 class="cta-title">Pronto para começar?</h2>
        <p class="cta-sub">
          Crie sua conta gratuitamente e comece a anotar agora.
        </p>
        <button
          class="nav-btn nav-btn--solid cta-btn"
          type="button"
          @click="goToLogin"
        >
          Criar conta grátis
        </button>
      </div>
    </section>
    <!-- ── FIM seção CTA ──────────────────────────────────────────────────── -->

    <footer class="footer">
      <div class="container footer-inner">
        <div class="footer-brand-wrap">
          <div class="footer-brand">
            <img
              src="/litterae_pena_fundo_transparente.png"
              alt="Litterae"
              class="footer-logo"
            />
            <div>
              <div class="footer-name">Litterae</div>
              <div class="footer-muted">Você não lê sozinho.</div>
            </div>
          </div>

          <div class="footer-company">© {{ year }} Gradus Tecnologia Ltda</div>
        </div>

        <div class="footer-links">
          <RouterLink :to="{ name: 'TermsOfUse' }" class="footer-link-legal">Termos de Uso</RouterLink>
          <RouterLink :to="{ name: 'PrivacyPolicy' }" class="footer-link-legal">Política de Privacidade</RouterLink>
        </div>
      </div>
    </footer>

    <Teleport to="body">
      <!-- ── PATCH: shot-modal com navegação por setas (sem botão fechar) ── -->
      <div
        v-if="activeShot !== null"
        class="shot-modal"
        role="dialog"
        aria-modal="true"
        tabindex="0"
        @click="closeShot"
        @wheel.prevent
        @touchmove.prevent
      >
        <div class="shot-modal-inner" @click.stop>

          <!-- Seta esquerda -->
          <button
            class="shot-modal-nav shot-modal-nav--prev"
            type="button"
            aria-label="Imagem anterior"
            @click.stop="shotPrev"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>

          <img
            :src="dashSlides[activeShot].src"
            :alt="dashSlides[activeShot].alt"
            class="shot-modal-img"
          />

          <!-- Seta direita -->
          <button
            class="shot-modal-nav shot-modal-nav--next"
            type="button"
            aria-label="Próxima imagem"
            @click.stop="shotNext"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>

        </div>
      </div>
      <!-- ── FIM PATCH: shot-modal carrossel ── -->
    </Teleport>

    <!-- ── PATCH: modal para imagens avulsas (audience-card, sem navegação) ── -->
    <Teleport to="body">
      <div
        v-if="activeSingleShot"
        class="shot-modal"
        role="dialog"
        aria-modal="true"
        tabindex="0"
        @click="closeSingleShot"
        @wheel.prevent
        @touchmove.prevent
      >
        <div class="shot-modal-inner" @click.stop>
          <img
            :src="activeSingleShot.src"
            :alt="activeSingleShot.alt"
            class="shot-modal-img"
          />
        </div>
      </div>
    </Teleport>
    <!-- ── FIM PATCH ── -->

    <Teleport to="body">
      <div
        v-if="activeVideo"
        class="video-modal"
        role="dialog"
        aria-modal="true"
        tabindex="0"
        @click="closeVideo"
        @wheel.prevent
        @touchmove.prevent
      >
        <div class="video-modal-inner" @click.stop>
          <div class="video-modal-head">
            <div class="video-modal-title">
              <span class="video-chip" aria-hidden="true">
                <i class="fa-solid fa-play"></i>
              </span>
              <div>
                <div class="video-title">{{ videoModalTitle }}</div>
                <div class="video-sub">{{ videoModalSub }}</div>
              </div>
            </div>

            <button
              class="video-modal-close"
              type="button"
              @click="closeVideo"
              @keydown.enter="closeVideo"
              @keydown.space.prevent="closeVideo"
              aria-label="Fechar"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="video-frame">
            <video
              v-if="isLocalVideo"
              ref="videoElRef"
              :src="howItWorksEmbedUrl"
              controls
              autoplay
              playsinline
              class="local-video-element"
              @loadedmetadata="onVideoLoaded"
              @timeupdate="onVideoTimeUpdate"
            ></video>

            <iframe
              v-else-if="howItWorksEmbedUrl"
              :src="howItWorksEmbedUrl"
              title="Vídeo do fluxo do Litterae"
              frameborder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowfullscreen
              referrerpolicy="strict-origin-when-cross-origin"
              class="embed-video-element"
            ></iframe>
            
            <div v-else class="video-fallback">
              Defina <strong>howItWorksVideoUrl</strong> com a URL do YouTube.
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css");

.public-home {
  min-height: 100vh;
  background: var(--bg-main);
  color: var(--text-dark);
  font-family: var(--font-family);
}

#pricing,
#how-it-works,
#how-it-helps,
#audience,
#community,
#search {
  scroll-margin-top: 150px;
}

.container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 18px;
}

.section {
  padding: 56px 0;
}

.section-after-hero {
  padding-top: 18px;
  padding-bottom: 18px;
}

.section--alt {
  background: var(--bg-content);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.section--alt-off {
  background: transparent;
  border-top: none;
  border-bottom: none;
}

.section--plain {
  background: var(--bg-main);
  border-top: none;
  border-bottom: none;
}

.section-divider {
  height: 1px;
  width: 100%;
  background: var(--border-color);
}

.section-head {
  margin-bottom: 22px;
}

.section-head h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.section-head p {
  margin: 8px 0 0;
  color: var(--text-light);
  line-height: 1.6;
}

.demo-video-wrap {
  margin-top: 16px;
  text-align: center;
}

.how-video-cta {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 14px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-dark);
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.how-video-cta--audience {
  margin-top: 0;
}

.how-video-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 18px rgba(0, 0, 0, 0.08);
}

.how-video-cta i {
  color: var(--text-on-brand);
  opacity: 0.9;
}

.how-video-cta--chip {
  margin-top: 12px;
  padding: 8px 11px;
  border-radius: 999px;
  font-size: 0.92rem;
}

/* ---------- Topbar ---------- */
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--bg-sidebar-left);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.topbar-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
}

/* ── PATCH: RouterLink da brand — sem decoração de link ── */
a.topbar-brand {
  text-decoration: none;
  color: inherit;
}
/* ── FIM PATCH ── */

.topbar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 0 1 auto;
}

.topbar-section-links {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}

.nav-links {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.mobile-menu {
  display: none;
  position: relative;
  margin-left: auto;
  flex: 0 0 auto;
}

.mobile-menu-toggle {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.mobile-menu-toggle:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
}

.mobile-menu-toggle:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand);
}

.mobile-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 80;
  width: min(280px, calc(100vw - 28px));
  padding: 10px;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  background: var(--bg-main);
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.14);
}

.mobile-dropdown-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border-radius: 13px;
  color: var(--text-dark);
  text-decoration: none;
  font-weight: 800;
  line-height: 1.2;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
}

.mobile-dropdown-link:hover {
  background: rgba(255, 255, 255, 0.75);
  border-color: var(--border-color);
  transform: translateY(-1px);
}

.mobile-dropdown-divider {
  height: 1px;
  margin: 8px 2px;
  background: var(--border-color);
}

.mobile-dropdown-link--ghost {
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  border-color: var(--border-color);
}

.mobile-dropdown-link--solid {
  justify-content: center;
  margin-top: 8px;
  background: var(--brand-light);
  color: var(--text-on-brand);
  border-color: transparent;
}

.topbar-link {
  text-decoration: none;
  font-weight: 800;
  color: var(--text-light);
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
  white-space: nowrap;
}

.topbar-link:hover {
  background: rgba(255, 255, 255, 0.55);
  border-color: var(--border-color);
  transform: translateY(-1px);
}

.brand-logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  border-radius: 12px;
  flex: 0 0 auto;
}

.brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.brand-name {
  font-weight: 800;
  font-size: 1.1rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.brand-tagline {
  font-size: 0.85rem;
  color: var(--text-light);
  line-height: 1.2;
}

.nav-btn {
  text-decoration: none;
  font-weight: 700;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
  font-size: 0.95rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-btn--ghost {
  background: rgba(255, 255, 255, 0.65);
  color: var(--text-dark);
}

.nav-btn--solid {
  background: var(--brand-light);
  color: var(--text-on-brand);
  border-color: transparent;
}

.nav-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
}

/* ---------- Hero ---------- */
.hero {
  padding: 34px 0 8px;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: stretch;
  justify-items: center;
}

.hero-left {
  padding: 22px;
  max-width: 980px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
}

.hero-title {
  margin: 14px 0 10px;
  font-size: 2.5rem;
  line-height: 1.08;
  letter-spacing: -0.03em;
  font-weight: 600;
}

.hero-subtitle {
  margin: 0 auto;
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--text-light);
  max-width: 68ch;
}

.hero-cta {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
  justify-content: center;
}

.hero-login {
  margin-top: 14px;
  color: var(--text-light);
  text-align: center;
}

.login-link {
  color: inherit;
  text-decoration: underline;
}

.hero-right {
  display: block;
  background: var(--bg-content);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.hero-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.08);
}

/* ---------- Cards & grids ---------- */
.cards-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 900px) {
  .cards-3 {
    grid-template-columns: 1fr;
  }
}

.info-card {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
}

.audience-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.audience-card--hover {
  cursor: default;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
  position: relative;
}

.audience-card--hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.08);
  border-color: rgba(189, 178, 255, 0.65);
}

.audience-card--hover:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand),
    0 16px 26px rgba(0, 0, 0, 0.08);
}

.audience-card--hover::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 18px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  background: radial-gradient(
    420px 180px at 92% 18%,
    rgba(189, 178, 255, 0.22),
    transparent 60%
  );
}

.audience-card--hover:hover::after {
  opacity: 1;
}

.info-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(160, 196, 255, 0.35);
  border: 1px solid rgba(160, 196, 255, 0.55);
  margin-bottom: 10px;
}

.info-card h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 900;
}

.info-card p {
  margin: 6px 0 0;
  color: var(--text-light);
  line-height: 1.6;
}

.info-card--video {
  margin-top: 16px;
  cursor: default;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
  position: relative;
}

.info-card--video:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.08);
  border-color: rgba(189, 178, 255, 0.65);
}

.info-card--video:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand),
    0 16px 26px rgba(0, 0, 0, 0.08);
}

.info-card--video::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 18px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  background: radial-gradient(
    420px 180px at 92% 18%,
    rgba(189, 178, 255, 0.22),
    transparent 60%
  );
}

.info-card--video:hover::after {
  opacity: 1;
}

.help-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 980px) {
  .help-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 620px) {
  .help-grid {
    grid-template-columns: 1fr;
  }
}

.help-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 14px;
}

/* ---------- Flow ---------- */
.flow-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 44px;
  row-gap: 44px;
  position: relative;

  --flow-col-gap: 44px;
  --flow-row-gap: 44px;
  --flow-arrow-len: 24px;
  --flow-arrow-thick: 4px;
}

.step {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
}

.flow-step {
  position: relative;
}

.flow-step--row2-col3 {
  grid-column: 3;
  grid-row: 2;
}

.flow-step--row2-col2 {
  grid-column: 2;
  grid-row: 2;
}

.flow-step--video {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.flow-step--video:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.08);
  border-color: rgba(189, 178, 255, 0.65);
}

.flow-step--video:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand),
    0 16px 26px rgba(0, 0, 0, 0.08);
}

.flow-step--video::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 18px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  background: radial-gradient(
    420px 180px at 92% 18%,
    rgba(189, 178, 255, 0.22),
    transparent 60%
  );
}

.flow-step--video:hover::after {
  opacity: 1;
}

.step-top {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.step-num {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  background: rgba(189, 178, 255, 0.22);
  border: 1px solid rgba(189, 178, 255, 0.4);
  color: var(--text-on-brand);
}

.step-title {
  margin: 0;
  font-weight: 900;
  font-size: 1.05rem;
  line-height: 1.25;
}

.step-desc {
  margin-top: 6px;
  color: var(--text-light);
  line-height: 1.6;
  font-size: 0.95rem;
}

.flow-arrow {
  position: absolute;
  pointer-events: none;
  opacity: 0.9;
  z-index: 2;
}

.flow-arrow--right {
  top: 50%;
  right: calc((var(--flow-col-gap) + var(--flow-arrow-len)) / -2);
  transform: translateY(-50%);
  width: var(--flow-arrow-len);
  height: var(--flow-arrow-thick);
  background: rgba(26, 34, 56, 0.28);
  border-radius: 999px;
}

.flow-arrow--right::after {
  content: "";
  position: absolute;
  right: -4px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  border-right: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-top: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-radius: 1px;
}

.flow-arrow--left {
  top: 50%;
  left: calc((var(--flow-col-gap) + var(--flow-arrow-len)) / -2);
  transform: translateY(-50%);
  width: var(--flow-arrow-len);
  height: var(--flow-arrow-thick);
  background: rgba(26, 34, 56, 0.28);
  border-radius: 999px;
}

.flow-arrow--left::after {
  content: "";
  position: absolute;
  left: -4px;
  top: 50%;
  transform: translateY(-50%) rotate(-45deg);
  width: 10px;
  height: 10px;
  border-left: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-top: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-radius: 1px;
}

.flow-arrow--down {
  left: 50%;
  top: 100%;
  --flow-arrow-head: 10px;
  --flow-arrow-head-overhang: 6px;

  transform: translateX(-50%)
    translateY(
      calc(
        (
            var(--flow-row-gap) -
              (var(--flow-arrow-len) + var(--flow-arrow-head-overhang))
          ) / 2
      )
    );

  width: var(--flow-arrow-thick);
  height: var(--flow-arrow-len);
  background: rgba(26, 34, 56, 0.28);
  border-radius: 999px;
}

.flow-arrow--down::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -4px;
  transform: translateX(-50%) rotate(45deg);
  width: var(--flow-arrow-head);
  height: var(--flow-arrow-head);
  border-right: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-bottom: var(--flow-arrow-thick) solid rgba(26, 34, 56, 0.28);
  border-radius: 1px;
}

@media (max-width: 980px) {
  .flow-steps {
    grid-template-columns: repeat(2, 1fr);
  }

  .flow-arrow {
    display: none;
  }

  .flow-step--row2-col3,
  .flow-step--row2-col2 {
    grid-column: auto;
    grid-row: auto;
  }
}

@media (max-width: 560px) {
  .flow-steps {
    grid-template-columns: 1fr;
  }
}

/* ---------- Pricing ---------- */
.pricing-section {
  padding: 60px 20px;
  max-width: none;
  margin: 0;
  background: var(--bg-main);
}

.pricing-inner {
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 0;
  color: var(--text-light);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.section-title {
  text-align: center;
  margin-bottom: 52px;
}

.section-title h2 {
  font-size: 2rem;
  font-weight: 700;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}

.price-card {
  background: var(--bg-content);
  padding: 18px;
  border-radius: 22px;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  position: relative;
  min-height: unset;
}

.price-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.price-card.featured {
  border: 2px solid var(--brand-light);
  transform: scale(1.05);
}

.popular-tag {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--brand-light);
  color: var(--text-on-brand);
  padding: 4px 15px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 700;
}

.card-header h3 {
  font-size: 1.5rem;
  margin-bottom: 6px;
}

.description {
  color: var(--text-light);
  line-height: 1.6;
  margin-top: 2px;
  margin-bottom: 6px;
}

.price {
  font-size: 2rem;
  font-weight: 800;
  margin: 12px 0;
}

.price span {
  font-size: 0.9rem;
  color: var(--text-light);
}

.features {
  list-style: none;
  padding: 0;
  margin: 0 0 14px 0;
  flex-grow: 1;
}

.features li {
  font-size: 0.92rem;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.features i {
  color: var(--success);
  margin-top: 2px;
}

.btn-plan {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-plan:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 18px rgba(0, 0, 0, 0.08);
}

.btn-plan:disabled {
  opacity: 0.95;
}

.btn-pro {
  background: #ffd6a5;
  color: #6a4b1f;
}

.btn-plus {
  background: var(--brand-light);
  color: #2a2457;
}

.btn-free {
  background: #d1e8d1;
  color: #215921;
}

.btn-edu {
  background: #ffadad;
  color: #7a1f1f;
}

.btn-premium {
  background: #ffadad;
  color: #7a1f1f;
}

.btn-other {
  background: var(--neutral-200);
  color: var(--text-dark);
}

.footer {
  padding: 22px 0 34px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-main);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}

.footer-brand-wrap {
  display: flex;
  align-items: center;
  gap: 32px;
  min-width: 0;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.footer-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.footer-name {
  font-weight: 900;
}

.footer-muted,
.footer-company {
  color: var(--text-light);
  font-size: 0.9rem;
}

.footer-company {
  white-space: nowrap;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-links a {
  color: var(--text-light);
  text-decoration: none;
  font-weight: 800;
}

.footer-links a:hover {
  text-decoration: underline;
}

/* -------- Modais -------- */
.shot-modal {
  position: fixed;
  inset: 0;
  z-index: 99999;
  padding: 18px;
  background: var(--backdrop);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  touch-action: none;
}

:global(html.modal-open),
:global(body.modal-open) {
  overflow: hidden !important;
}

:global(html),
:global(body) {
  background: var(--bg-main);
}

.shot-modal-inner {
  position: relative;
  width: min(1100px, 96vw);
  max-height: 92vh;
  background: var(--surface);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
}

.shot-modal-img {
  width: 100%;
  height: auto;
  max-height: 92vh;
  display: block;
  object-fit: contain;
  background: #0b0f1a;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* ── PATCH: botões de navegação no modal de imagem ampliada ── */
.shot-modal-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.52);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  z-index: 3;
  outline: none;
  transition: background 0.15s, transform 0.15s;
}

.shot-modal-nav:hover {
  background: rgba(0, 0, 0, 0.75);
  transform: translateY(-50%) scale(1.08);
}

.shot-modal-nav:focus-visible {
  box-shadow: var(--focus-ring-brand);
}

.shot-modal-nav--prev { left: 12px; }
.shot-modal-nav--next { right: 12px; }
/* ── FIM PATCH ── */

.video-modal {
  position: fixed;
  inset: 0;
  z-index: 100000;
  padding: 18px;
  background: rgba(10, 14, 26, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  touch-action: none;
}

@supports not (
  (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))
) {
  .video-modal {
    background: rgba(10, 14, 26, 0.24);
  }
}

.video-modal-inner {
  width: min(1280px, 96vw);
  max-height: 92vh;
  background: var(--surface);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.38);
}

.video-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 14px;
  border-bottom: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.78);
}

.video-modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.video-chip {
  width: 42px;
  height: 42px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(189, 178, 255, 0.28);
  border: 1px solid rgba(189, 178, 255, 0.45);
  color: var(--text-on-brand);
  flex: 0 0 auto;
}

.video-chip i {
  transform: translateX(1px);
}

.video-title {
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.15;
}

.video-sub {
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.2;
}

.video-modal-close {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
}

.video-modal-close:hover {
  background: rgba(255, 255, 255, 0.95);
}

.video-modal-close:focus-visible {
  box-shadow: var(--focus-ring-brand);
}

/* Container garantindo que o modal nunca colapse (mantém o formato 16:9 clássico de vídeos) */
.video-frame {
  position: relative;
  width: 100%;
  height: min(72vh, calc(96vw * 9 / 16));
  background: #0b0f1a;
  overflow: hidden;
}

/* O vídeo e o iframe ocuparão rigorosamente todo o espaço do frame */
.local-video-element,
.embed-video-element {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  object-fit: cover;
}

.video-frame iframe,
.video-frame video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.video-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 800;
  padding: 0 14px;
  text-align: center;
}

/* ---------- FAQ ---------- */
.faq {
  display: grid;
  gap: 10px;
}

.faq-item {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
}

.faq-q {
  padding: 14px 14px;
  font-weight: 900;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  list-style: none;
}

.faq-q::-webkit-details-marker {
  display: none;
}

.faq-a {
  padding: 0 14px 14px;
  color: var(--text-light);
  line-height: 1.65;
}

.hero-cta-btn {
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 1.05rem;
  font-weight: 800;
  gap: 12px;
}

.hero-cta-btn i {
  transform: translateY(1px);
}

.pricing-section .section-title h2 {
  font-size: 1.65rem;
}

.pricing-section .section-title p {
  font-size: 0.95rem;
}

.pricing-section .card-header h3 {
  font-size: 1.28rem;
}

.pricing-section .description {
  font-size: 0.9rem;
  line-height: 1.5;
}

.pricing-section .price {
  font-size: 1.65rem;
}

.pricing-section .price span {
  font-size: 0.85rem;
}

.pricing-section .features li {
  font-size: 0.85rem;
  line-height: 1.35;
}

.pricing-section .btn-plan {
  font-size: 0.92rem;
}

/* ---------- Topbar responsiva ---------- */
@media (max-width: 900px) {
  .topbar-inner {
    position: relative;
    justify-content: space-between;
    gap: 12px;
  }

  .topbar-brand {
    flex: 1 1 auto;
    min-width: 0;
  }

  .topbar-section-links,
  .nav-links {
    display: none;
  }

  .mobile-menu {
    display: block;
  }
}

@media (max-width: 640px) {
  .topbar-inner {
    padding: 12px 14px;
    gap: 10px;
  }

  .topbar-brand {
    align-items: center;
  }

  .brand-logo {
    width: 40px;
    height: 40px;
  }

  .brand-name {
    font-size: 1rem;
  }

  .brand-tagline {
    font-size: 0.8rem;
  }

  .mobile-menu-toggle {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

  .footer-inner {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 14px;
  }

  .footer-brand-wrap {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }

  .footer-company {
    display: block;
    white-space: nowrap;
  }

  .footer-links {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .hero {
    padding: 22px 0 6px;
  }

  .hero-left {
    padding: 14px 4px;
  }

  .hero-title {
    margin: 8px 0 10px;
    font-size: 1.9rem;
    line-height: 1.12;
  }

  .hero-subtitle {
    font-size: 0.98rem;
    line-height: 1.6;
  }

  .hero-cta-btn {
    width: 100%;
    justify-content: center;
  }

  .section {
    padding: 42px 0;
  }

  .section-head h2 {
    font-size: 1.45rem;
  }

  .pricing-section {
    padding: 44px 16px;
  }

  .section-title {
    margin-bottom: 34px;
  }

  .section-title h2 {
    font-size: 1.5rem;
  }

  .video-modal {
    padding: 10px;
  }

  .video-modal-head {
    padding: 12px;
  }

  .video-title {
    font-size: 0.96rem;
  }

  .video-sub {
    font-size: 0.82rem;
  }
}

@media (max-width: 420px) {
  .topbar-inner {
    padding: 10px 12px;
  }

  .brand-text {
    min-width: 0;
  }

  .brand-tagline {
    white-space: normal;
  }

  .mobile-dropdown {
    width: min(270px, calc(100vw - 24px));
  }

  .container {
    padding: 0 14px;
  }

  .hero-title {
    font-size: 1.7rem;
  }

  .price-card {
    padding: 16px;
  }
}

/* ── link legal no rodapé ── */
.footer-link-legal {
  color: var(--text-light);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
  opacity: 0.8;
  transition: opacity 0.15s;
}
.footer-link-legal:hover {
  opacity: 1;
  text-decoration: underline;
}

/* ── seção CTA de encerramento ── */
.cta-section {
  padding: 64px 20px;
  background: var(--bg-sidebar-left);
  border-top: 1px solid var(--border-color);
  text-align: center;
}

.cta-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.cta-title {
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
}

.cta-sub {
  font-size: 1rem;
  color: var(--text-light);
  margin: 0;
}

.cta-btn {
  padding: 14px 28px;
  font-size: 1rem;
  font-weight: 800;
  border-radius: 16px;
}

/* ── badge Acesso imediato ── */
.free-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 8px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #d1e8d1;
  color: #215921;
  font-size: 0.78rem;
  font-weight: 600;
}

.free-badge i {
  font-size: 0.75rem;
}

.help-card-shot {
  margin-top: 0.75rem;
  width: 100%;
  height: auto;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  display: block;
}
.audience-card-shot {
  margin-top: 0.75rem;
  width: 100%;
  height: auto;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  display: block;
}
.audience-highlight {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: rgba(189, 178, 255, 0.15);
  border: 1px solid rgba(189, 178, 255, 0.45);
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-on-brand);
  line-height: 1.5;
}

.audience-highlight__icon {
  flex-shrink: 0;
  margin-top: 0.15rem;
  color: var(--text-brand);
  font-size: 0.9rem;
}

/* ── imagens clicáveis ── */
.audience-card-shot,
.help-card-shot {
  cursor: zoom-in;
  transition: opacity 0.15s, transform 0.15s;
}

.audience-card-shot:hover,
.help-card-shot:hover {
  opacity: 0.9;
  transform: scale(1.01);
}

/* ── seção Comunidade ─────────────────────────────────────────────── */

.community-section {
  background: var(--bg-content);
  border-bottom: none;
  padding-bottom: 50px;
}

.community-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  margin-bottom: 0;
}

.faq-section {
  background: var(--bg-content);
}

@media (max-width: 860px) {
  .community-showcase {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}

.community-copy__lead {
  margin: 0 0 16px;
  font-weight: 600;
  color: var(--text-dark);
  line-height: 1.6;
}

.community-examples {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.community-example {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.93rem;
  line-height: 1.65;
  color: var(--text-light);
}

.community-example strong {
  color: var(--text-dark);
}

.community-example__icon {
  flex-shrink: 0;
  margin-top: 2px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(189, 178, 255, 0.18);
  border: 1px solid rgba(189, 178, 255, 0.38);
  color: #4a3f8f;
  font-size: 0.82rem;
}

.community-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  font-size: 0.95rem;
  font-weight: 800;
  border-radius: 14px;
}

.community-shot-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.community-shot-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-light);
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.community-shot-label i {
  color: #4a3f8f;
  font-size: 0.8rem;
}

/* ── carrossel do dashboard ── */
.dash-carousel {
  position: relative;
  width: 100%;
}

.dash-carousel__img {
  width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.09);
  cursor: zoom-in;
  display: block;
  transition: opacity 0.2s, box-shadow 0.15s;
}

.dash-carousel__img:hover {
  opacity: 0.96;
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.13);
}

.dash-carousel__btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(26, 34, 56, 0.52);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 0.15s, transform 0.15s;
  z-index: 2;
  outline: none;
}

.dash-carousel__btn:hover {
  background: rgba(26, 34, 56, 0.78);
  transform: translateY(-50%) scale(1.08);
}

.dash-carousel__btn:focus-visible {
  box-shadow: var(--focus-ring-brand);
}

.dash-carousel__btn--prev {
  left: 10px;
}

.dash-carousel__btn--next {
  right: 10px;
}

.dash-carousel__dots {
  display: flex;
  justify-content: center;
  gap: 7px;
  margin-top: 2px;
}

.dash-carousel__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid rgba(26, 34, 56, 0.25);
  background: rgba(26, 34, 56, 0.15);
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, transform 0.15s;
  outline: none;
}

.dash-carousel__dot--active {
  background: #4a3f8f;
  border-color: #4a3f8f;
  transform: scale(1.25);
}

.dash-carousel__dot:focus-visible {
  box-shadow: var(--focus-ring-brand);
}

.community-shot {
  width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.09);
  cursor: zoom-in;
  display: block;
  transition: opacity 0.15s, transform 0.15s, box-shadow 0.15s;
}

.community-shot:hover {
  opacity: 0.96;
  transform: scale(1.01);
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.13);
}

.community-shot-hint {
  margin: 0;
  font-size: 0.78rem;
  color: var(--text-light);
  display: flex;
  align-items: center;
  gap: 5px;
  opacity: 0.7;
}

.community-cards {
  margin-top: 8px;
  margin-bottom: 40px;
}

.community-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.community-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.08);
  border-color: rgba(189, 178, 255, 0.65);
}

.community-icon--follow {
  background: rgba(189, 178, 255, 0.22);
  border-color: rgba(189, 178, 255, 0.45);
}

.community-icon--feed {
  background: rgba(160, 210, 255, 0.22);
  border-color: rgba(160, 210, 255, 0.45);
}

.community-icon--rank {
  background: rgba(160, 230, 180, 0.22);
  border-color: rgba(160, 230, 180, 0.45);
}

/* ── seção Clube de Leitura ───────────────────────────────────────── */

#reading-club {
  scroll-margin-top: 150px;
}

/* Narrativa justificada */
.reading-club__narrative {
  
  line-height: 1.75;
  color: var(--text-light);
  margin: 0 0 32px;
  font-size: 0.97rem;
  text-align: justify;
}

/* Passos horizontais */
.reading-club__steps {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 28px;
}

@media (max-width: 900px) {
  .reading-club__steps {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 560px) {
  .reading-club__steps {
    grid-template-columns: 1fr 1fr;
  }
}

.rc-step {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 14px 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Proporcional ao .step-num e .info-icon (44×44px, border-radius 16px) */
.rc-step__num {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 0.95rem;
  background: rgba(189, 178, 255, 0.22);
  border: 1px solid rgba(189, 178, 255, 0.4);
  color: var(--text-on-brand);
  flex-shrink: 0;
}

/* Proporcional ao .step-desc (0.95rem / line-height 1.6) */
.rc-step__text {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  line-height: 1.6;
}

/* ── seção Pesquisa detalhada ─────────────────────────────────────── */

.search-cards {
  margin-bottom: 28px;
}

.search-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.search-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 26px rgba(0, 0, 0, 0.08);
  border-color: rgba(189, 178, 255, 0.65);
}

.search-card--featured {
  border: 2px solid rgba(189, 178, 255, 0.55);
}

.search-card-badge {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--brand-light);
  color: var(--text-on-brand);
  padding: 3px 14px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

/* Ícones coloridos por entidade */
.search-icon--texts {
  background: rgba(160, 196, 255, 0.28);
  border-color: rgba(160, 196, 255, 0.5);
}
.search-icon--activities {
  background: rgba(160, 230, 180, 0.28);
  border-color: rgba(160, 230, 180, 0.5);
}
.search-icon--notes {
  background: rgba(189, 178, 255, 0.28);
  border-color: rgba(189, 178, 255, 0.5);
}

/* Lista de filtros dentro do card */
.search-filter-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: auto;
}

.search-filter-list li {
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-size: 0.8rem;
  color: var(--text-light);
  line-height: 1.45;
}

.search-filter-list i {
  font-size: 0.45rem;
  color: var(--brand-light);
  flex-shrink: 0;
  margin-top: 4px;
}

/* ── Bloco de exemplos práticos ── */
.search-examples-wrap {
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.search-examples-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text-on-brand);
}

.search-examples-title i {
  color: var(--text-brand);
  font-size: 0.9rem;
  flex-shrink: 0;
}

.search-examples-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 900px) {
  .search-examples-grid {
    grid-template-columns: 1fr;
  }
}

.search-example-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(189, 178, 255, 0.1);
  border: 1px solid rgba(189, 178, 255, 0.38);
  border-radius: 14px;
  padding: 14px;
}

.search-example-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(189, 178, 255, 0.22);
  border: 1px solid rgba(189, 178, 255, 0.45);
  color: #4a3f8f;
  font-size: 0.82rem;
}

.search-example-card p {
  margin: 0;
  font-size: 0.83rem;
  color: var(--text-light);
  line-height: 1.65;
}

.search-example-card strong {
  color: var(--text-dark);
  font-weight: 700;
}

.search-example-card em {
  font-style: italic;
}

/* ── Carrossel da pesquisa ── */
.search-carousel-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-carousel-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-light);
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin: 0;
}

.search-carousel-label i {
  color: #4a3f8f;
  font-size: 0.8rem;
}

.search-carousel {
  position: relative;
  width: 100%;
}

.search-carousel__slides {
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
}

.search-carousel__img {
  width: 100%;
  height: auto;
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.09);
  cursor: default;
  display: block;
  transition: opacity 0.2s, box-shadow 0.15s;
}

.search-carousel__img:hover {
  opacity: 0.96;
  box-shadow: 0 20px 44px rgba(0, 0, 0, 0.13);
}

.search-carousel__dots-row {
  margin-top: 2px;
}

.search-carousel__slide-caption {
  margin: 0;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-light);
  font-style: italic;
  opacity: 0.85;
}
</style>
